/**
 * expressionParser.js
 *
 * Lossless, display-only parser for automation value expressions.
 *
 * IMPORTANT CONTRACT
 * - This parser is a READ-ONLY MIRROR of the backend runtime resolver
 *   (backend/functions/core/utils/variableResolver.js — resolveAutomationValue).
 *   It never resolves anything; it only tokenizes a saved string so the editor
 *   can render chips and classify values.
 * - Round-trip identity is guaranteed by construction:
 *     serializeSegments(parseExpression(raw).segments) === raw
 *   Every character of the input is covered by exactly one segment, and each
 *   segment keeps its original raw slice.
 * - Anything the parser does not understand stays a plain `text` segment.
 *   The editor must keep such values untouched (raw/advanced mode).
 *
 * Runtime semantics mirrored here (do not "fix" without changing the backend):
 * - A string starting with `$` is ALWAYS a whole-value variable reference
 *   (type preserved), even if it contains spaces (kind: 'wholeDollar').
 * - A string starting with `@` with no space and no dash is a whole-value
 *   field reference (type preserved) (kind: 'wholeAt').
 * - Everything else is a template: `{{path}}`, `@todayDate…`, `@invoiceDate…`,
 *   inline `@field` (single \w+ segment) and `$var` are interpolated as text.
 * - `%token%` is NOT resolved by the main resolver (only email-recipient code
 *   handles two hardcoded tokens; unknown %tokens% are skipped there). We
 *   tokenize them so the editor can display a read-only "legacy" chip.
 */

export const SEGMENT_TYPES = {
  TEXT: 'text',
  BRACE: 'brace', // {{path}}
  AT_DATE: 'atDate', // @todayDate…, @invoiceDate…
  AT_FIELD: 'atField', // inline @field (legacy)
  DOLLAR: 'dollar', // $var / $var.path
  PERCENT: 'percent', // %token% (legacy email)
};

export const PATH_SOURCES = {
  DATE: 'date', // todayDate…
  INVOICE_DATE: 'invoiceDate', // invoiceDate…
  SYSTEM: 'system', // elementId / workflowId / businessId
  ASYNC: 'async', // requires a Firestore lookup at runtime
  FIELD: 'field', // plain elementData dot-path
};

// Async tokens supported by the backend resolver (ASYNC_TOKENS map).
const ASYNC_TOKEN_PATHS = ['assignedToEmail', 'dependency.data.Cou_field271'];

const SYSTEM_PATHS = ['elementId', 'workflowId', 'businessId'];

// Date-tag grammar — copied from resolveDateTag / resolveInvoiceDateTag.
const TODAY_TAG_RE = /^todayDate(?:\*Z\*|[+-]\d+(?:\*Z\*)?|(?:[+-]\d+)?@\d{2}:\d{2}(?:\*Z\*)?)?$/;
const INVOICE_TAG_RE = /^invoiceDate(?:\*Z\*|[+-]\d+(?:\*Z\*)?)?$/;

/**
 * Combined tokenizer. Alternation order mirrors the runtime resolution order:
 * {{path}} first, then @todayDate/@invoiceDate variants (before plain @field,
 * exactly like resolveAutomationString), then inline @field, $var, %token%.
 * Runtime regexes mirrored:
 *   /\{\{([^}]+)\}\}/g
 *   /@todayDate([+-]\d+)?(@\d{2}:\d{2})?(\*Z\*)?/g
 *   /@invoiceDate([+-]\d+)?(\*Z\*)?/g
 *   /@(\w+)/g
 *   $var: split/join on known variable names (approximated here as a
 *   $name(.path)* token — the editor decides if the name matches a known
 *   step output).
 */
const TOKEN_RE = new RegExp(
  [
    '\\{\\{([^}]+)\\}\\}', // 1: brace path
    '@(todayDate(?:[+-]\\d+)?(?:@\\d{2}:\\d{2})?(?:\\*Z\\*)?)', // 2: today tag
    '@(invoiceDate(?:[+-]\\d+)?(?:\\*Z\\*)?)', // 3: invoice tag
    '@(\\w+)', // 4: inline at-field
    '\\$([A-Za-z_][\\w]*(?:\\.[\\w]+)*)', // 5: dollar var
    '%([\\w.]+)%', // 6: percent token
  ].join('|'),
  'g'
);

/**
 * Classify a path used inside {{...}} or after a whole-value @.
 * Returns { source, valid } where valid only applies to date grammars.
 */
export function classifyPath(path) {
  const trimmed = String(path || '').trim();
  if (trimmed.startsWith('todayDate')) {
    return { source: PATH_SOURCES.DATE, valid: TODAY_TAG_RE.test(trimmed) };
  }
  if (trimmed.startsWith('invoiceDate')) {
    return { source: PATH_SOURCES.INVOICE_DATE, valid: INVOICE_TAG_RE.test(trimmed) };
  }
  if (SYSTEM_PATHS.includes(trimmed)) {
    return { source: PATH_SOURCES.SYSTEM, valid: true };
  }
  if (ASYNC_TOKEN_PATHS.includes(trimmed)) {
    return { source: PATH_SOURCES.ASYNC, valid: true };
  }
  return { source: PATH_SOURCES.FIELD, valid: true };
}

function tokenizeInline(raw) {
  const segments = [];
  let lastIndex = 0;
  TOKEN_RE.lastIndex = 0;
  let match;
  while ((match = TOKEN_RE.exec(raw)) !== null) {
    if (match.index > lastIndex) {
      segments.push({ type: SEGMENT_TYPES.TEXT, raw: raw.slice(lastIndex, match.index) });
    }
    const tokenRaw = match[0];
    const precededByWordChar =
      match.index > 0 && /[\w.]/.test(raw[match.index - 1]);

    if (match[1] !== undefined) {
      const path = match[1].trim();
      segments.push({
        type: SEGMENT_TYPES.BRACE,
        raw: tokenRaw,
        path,
        ...classifyPath(path),
      });
    } else if (match[2] !== undefined || match[3] !== undefined) {
      const tag = match[2] !== undefined ? match[2] : match[3];
      segments.push({
        type: SEGMENT_TYPES.AT_DATE,
        raw: tokenRaw,
        path: tag,
        ...classifyPath(tag),
        precededByWordChar,
      });
    } else if (match[4] !== undefined) {
      // Inline @field. WARNING: like the runtime regex /@(\w+)/, this also
      // matches the domain part of email addresses ("user@example.com" →
      // "@example"). `precededByWordChar` lets the UI render those as text.
      segments.push({
        type: SEGMENT_TYPES.AT_FIELD,
        raw: tokenRaw,
        path: match[4],
        source: PATH_SOURCES.FIELD,
        precededByWordChar,
      });
    } else if (match[5] !== undefined) {
      segments.push({
        type: SEGMENT_TYPES.DOLLAR,
        raw: tokenRaw,
        name: match[5],
        precededByWordChar,
      });
    } else if (match[6] !== undefined) {
      segments.push({
        type: SEGMENT_TYPES.PERCENT,
        raw: tokenRaw,
        token: match[6],
        known: ASYNC_TOKEN_PATHS.includes(match[6]),
      });
    }
    lastIndex = match.index + tokenRaw.length;
  }
  if (lastIndex < raw.length) {
    segments.push({ type: SEGMENT_TYPES.TEXT, raw: raw.slice(lastIndex) });
  }
  return segments;
}

/**
 * Parse a saved automation value into displayable segments.
 *
 * @param {*} raw - The saved value (any type; only strings are parsed).
 * @returns {{
 *   raw: *,
 *   kind: 'nonString'|'empty'|'static'|'wholeDollar'|'wholeAt'|'template',
 *   segments: Array,
 *   warnings: string[],
 *   isWholeValue: boolean,   // runtime preserves the referenced value's type
 *   isSingleToken: boolean,  // exactly one token, no surrounding text
 * }}
 */
export function parseExpression(raw) {
  if (typeof raw !== 'string') {
    return {
      raw,
      kind: 'nonString',
      segments: [],
      warnings: [],
      isWholeValue: false,
      isSingleToken: false,
    };
  }

  if (raw === '') {
    return {
      raw,
      kind: 'empty',
      segments: [],
      warnings: [],
      isWholeValue: false,
      isSingleToken: false,
    };
  }

  // Whole-string $var — mirrors: if (value.startsWith('$')) { ... }
  // NOTE (characterized): the ENTIRE rest of the string is the variable name
  // (dot-path allowed), even if it contains spaces.
  if (raw.startsWith('$')) {
    const name = raw.substring(1);
    return {
      raw,
      kind: 'wholeDollar',
      segments: [{ type: SEGMENT_TYPES.DOLLAR, raw, name }],
      warnings: /\s/.test(name) ? ['dollarNameHasWhitespace'] : [],
      isWholeValue: true,
      isSingleToken: true,
    };
  }

  // Whole-string @field — mirrors:
  // if (value.startsWith('@') && !value.includes(' ') && !value.includes('-'))
  if (raw.startsWith('@') && !raw.includes(' ') && !raw.includes('-')) {
    const path = raw.substring(1);
    const classification = classifyPath(path);
    const type =
      classification.source === PATH_SOURCES.DATE ||
      classification.source === PATH_SOURCES.INVOICE_DATE
        ? SEGMENT_TYPES.AT_DATE
        : SEGMENT_TYPES.AT_FIELD;
    return {
      raw,
      kind: 'wholeAt',
      segments: [{ type, raw, path, ...classification }],
      warnings: classification.valid === false ? ['invalidDateTag'] : [],
      isWholeValue: true,
      isSingleToken: true,
    };
  }

  const segments = tokenizeInline(raw);
  const warnings = [];

  // Unbalanced/leftover braces in text segments: the editor must treat the
  // whole value as an advanced expression (display raw, never rewrite).
  const hasLooseBraces = segments.some(
    (s) => s.type === SEGMENT_TYPES.TEXT && (s.raw.includes('{{') || s.raw.includes('}}'))
  );
  if (hasLooseBraces) warnings.push('unbalancedBraces');

  segments.forEach((s) => {
    if (s.valid === false) warnings.push('invalidDateTag');
  });

  const tokenSegments = segments.filter((s) => s.type !== SEGMENT_TYPES.TEXT);
  const kind = tokenSegments.length > 0 ? 'template' : 'static';

  return {
    raw,
    kind,
    segments,
    warnings,
    // Templates ALWAYS produce strings at runtime, even a lone {{x}}.
    isWholeValue: false,
    isSingleToken: tokenSegments.length === 1 && segments.length === 1,
  };
}

/**
 * Serialize segments back to the exact saved string.
 * Identity guarantee: serializeSegments(parseExpression(raw).segments) === raw.
 */
export function serializeSegments(segments) {
  return (segments || []).map((s) => s.raw).join('');
}

// ─── Token builders (canonical syntax for NEW insertions only) ─────────────

/** {{path}} — canonical for fields, system tags and dates (string result). */
export function buildBraceToken(path) {
  return `{{${path}}}`;
}

/** $name / $name.path — canonical for previous step outputs. */
export function buildDollarToken(name, subPath) {
  return subPath ? `$${name}.${subPath}` : `$${name}`;
}

/** @path — legacy whole-value form; only for explicit "preserve type" choice. */
export function buildAtToken(path) {
  return `@${path}`;
}

/** True when the whole value is a single token whose runtime type is preserved. */
export function isTypePreservingValue(raw) {
  const parsed = parseExpression(raw);
  return parsed.isWholeValue;
}
